// .docx -> plain text, matching what Office.js `document.body.text` yields for
// the same file — that is what the Word add-in fingerprints, so this must agree
// with it or a manually attached copy of an add-in-certified document will not
// match.
//
// Reads word/document.xml ONLY. Headers, footers and footnotes live in separate
// parts (word/header1.xml, word/footnotes.xml), so excluding them is automatic —
// and correct, because `document.body` excludes them too.
//
// Two rules that are easy to get backwards and fail SILENTLY:
//   - <w:t> runs INSIDE a paragraph concatenate with NO separator. Word splits
//     a word across runs at spell-check/formatting boundaries.
//   - Every <w:p> boundary MUST emit a separator, or the last word of one
//     paragraph fuses with the first of the next.
// normalize() collapses [^\p{L}\p{N}]+ to a single space, so the IDENTITY of a
// separator is irrelevant — only its presence.

const TEXT_RUN = /<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>|<w:(tab|br|cr)\b[^>]*\/?>/g;
// Self-closing form FIRST: in `<w:p w:rsidR="x"/>` a greedy [^>]* happily eats
// the "/" too, so the open-tag branch would otherwise match it and then swallow
// everything up to the NEXT </w:p> — losing a paragraph boundary silently.
const PARAGRAPH = /<w:p(?:\s[^>]*)?\/>|<w:p(?:\s[^>]*)?>[\s\S]*?<\/w:p>/g;

function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&amp;/g, "&"); // last, so "&amp;lt;" does not become "<"
}

/** Text of one <w:p>. Runs concatenate with no separator; tabs/breaks separate. */
function paragraphText(xml: string): string {
  let out = "";
  for (const m of xml.matchAll(TEXT_RUN)) {
    if (m[1] !== undefined) out += decodeEntities(m[1]);
    else if (m[2] === "tab") out += "\t";
    else out += "\n";
  }
  return out;
}

export function paragraphsToText(paragraphs: string[]): string {
  return paragraphs.join("\n");
}

// A text box is a floating shape, but Word stores its paragraphs INSIDE
// document.xml, nested within a body paragraph. Left in place they cause three
// silent defects: PARAGRAPH's lazy match terminates on the BOX's </w:p>, so the
// outer paragraph's trailing runs are LOST; the text before the box fuses with
// the box's first word; and the box text is emitted TWICE, because Word writes
// it under both <mc:Choice> (wps:txbx) and <mc:Fallback> (v:textbox).
//
// VERIFIED against a live Word add-in digest (2026-08-31, ClickUp 86bbqzjcf):
// Office.js `body.text` covers the main story and floating-shape text lives in
// Shape.TextFrame, so excluding box text is what matches it — and table-cell
// text IS included. Pinned exactly by the fixture in
// __tests__/fixtures/table-and-textbox.docx via __tests__/docxAddInParity.test.ts,
// which reproduces the real add-in's fuzzy_digest for a document containing
// both a table and text boxes. Do NOT "restore" the box text — that would
// regress a confirmed match, not just an assumed one.
// Self-closing form first: a greedy [^>]* eats the "/" too, so the open-tag
// branch would otherwise claim <w:txbxContent .../> and open a phantom box.
const TEXT_BOX_TAG = /<w:txbxContent[^>]*\/>|<\/w:txbxContent>|<w:txbxContent[^>]*>/g;

// Depth-aware, because a single lazy regex CANNOT do this: with a box inside a
// box it pairs the OUTER open tag with the INNER close tag, deleting half the
// nesting and orphaning a </w:p> — which then closes the enclosing body
// paragraph early and loses its remaining runs. That is the very bug this strip
// exists to prevent, so it is worth the ten lines.
//
// The blob is user-supplied, so malformed markup is REACHABLE input; both
// unbalanced cases are handled explicitly rather than left to fall out:
//   - a stray close at depth 0 is left in the stream — it is neither <w:p> nor
//     <w:t>, so it is inert, and the surrounding text is kept;
//   - an OPEN that never closes runs to EOF, so everything after it is dropped.
//     `cursor` is only advanced when a box CLOSES, so without the depth check
//     below the tail `xml.slice(cursor)` would re-emit every byte already
//     flushed into `out` — duplicating the whole preceding document AND
//     restoring the box text, silently, in the one direction that matters.
// Both are pinned by tests; neither throws.
function stripTextBoxes(xml: string): string {
  let out = "";
  let depth = 0;
  let cursor = 0;
  for (const m of xml.matchAll(TEXT_BOX_TAG)) {
    const tag = m[0];
    const at = m.index;
    if (tag.startsWith("</")) {
      if (depth > 0 && --depth === 0) cursor = at + tag.length;
    } else if (tag.endsWith("/>")) {
      if (depth === 0) {
        out += xml.slice(cursor, at);
        cursor = at + tag.length;
      }
    } else if (depth++ === 0) {
      out += xml.slice(cursor, at);
    }
  }
  return depth > 0 ? out : out + xml.slice(cursor);
}

/** Plain text of a word/document.xml payload. */
export function textFromDocumentXml(xml: string): string {
  const body = stripTextBoxes(xml.slice(xml.indexOf("<w:body")));
  const paragraphs: string[] = [];
  for (const m of body.matchAll(PARAGRAPH)) paragraphs.push(paragraphText(m[0]));
  return paragraphsToText(paragraphs);
}

/** Unzip a .docx and extract word/document.xml. Returns null if unreadable. */
export async function extractDocxText(file: Blob): Promise<string | null> {
  try {
    const xml = await readZipEntry(file, "word/document.xml");
    return xml === null ? null : textFromDocumentXml(xml);
  } catch {
    return null;
  }
}

// Minimal ZIP reader: locate one stored/deflated entry by name via the central
// directory. A .docx is a ZIP; pulling in a full ZIP library for one entry is
// not worth the bundle weight.
async function readZipEntry(file: Blob, name: string): Promise<string | null> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const target = new TextEncoder().encode(name);

  // End of central directory: scan back for signature 0x06054b50.
  let eocd = -1;
  for (let i = buf.length - 22; i >= 0 && i > buf.length - 65558; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) return null;

  let p = dv.getUint32(eocd + 16, true); // central directory offset
  const count = dv.getUint16(eocd + 10, true);
  for (let i = 0; i < count; i++) {
    if (dv.getUint32(p, true) !== 0x02014b50) return null;
    const method = dv.getUint16(p + 10, true);
    const compSize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOff = dv.getUint32(p + 42, true);
    const nameBytes = buf.subarray(p + 46, p + 46 + nameLen);
    const matches = nameLen === target.length && nameBytes.every((b, j) => b === target[j]);
    if (matches) {
      // Local header: name/extra lengths differ from the central copy.
      const lNameLen = dv.getUint16(localOff + 26, true);
      const lExtraLen = dv.getUint16(localOff + 28, true);
      const start = localOff + 30 + lNameLen + lExtraLen;
      const raw = buf.subarray(start, start + compSize);
      if (method === 0) return new TextDecoder().decode(raw);
      if (method !== 8) return null;
      const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
      return new TextDecoder().decode(new Uint8Array(await new Response(stream).arrayBuffer()));
    }
    p += 46 + nameLen + extraLen + commentLen;
  }
  return null;
}
